import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { sessionsService, sessionRegistrationsService, coursesService } from '../../services/firestoreService';
import LiveCodeClassroom from '../classroom/LiveCodeClassroom';
import TopicInteractiveLearning from '../classroom/TopicInteractiveLearning';
import LiveVideoClassroom from '../classroom/LiveVideoClassroom';
import dailyVideoService from '../../services/dailyVideoService';
import { mergeSessionFeatureToggles } from '../../config/sessionFeatureToggles';
import './Classroom.css';

const TAB_KEYS = ['video', 'livecode', 'interactive'];
const TAB_LABELS = {
  video: '🎥 Video Classroom',
  livecode: '💻 Live Code',
  interactive: '🎮 Interactive'
};

const Classroom = () => {
  const { sessionId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const topicParam = searchParams.get('topic');
  const studentId = user?.id || user?.uid;
  const studentName = user?.name || user?.displayName || user?.email || 'Student';
  const [session, setSession] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    // Default to interactive tab (students will only see this anyway)
    // Admin/coach can switch tabs if needed
    return TAB_KEYS.includes(tabParam || '') ? tabParam : 'interactive';
  }); // 'video', 'livecode', 'interactive'
  const [course, setCourse] = useState(null);
  const [courseLoading, setCourseLoading] = useState(false);
  const [activeTopicName, setActiveTopicName] = useState('');
  const [interactiveStars, setInteractiveStars] = useState(0);
  const [featureToggles, setFeatureToggles] = useState(mergeSessionFeatureToggles());

  const isTabEnabled = useCallback((tabKey, toggles = featureToggles) => {
    // For students, only show interactive tab
    if (user?.type === 'student') {
      return tabKey === 'interactive';
    }
    // For admin/coach, use feature toggles
    switch (tabKey) {
      case 'video':
        return toggles.showVideoTab !== false;
      case 'livecode':
        return toggles.showLiveCodeTab !== false;
      case 'interactive':
        return toggles.showInteractiveTab !== false;
      default:
        return true;
    }
  }, [featureToggles, user?.type]);

  useEffect(() => {
    const loadClassroomData = async () => {
      if (!studentId || !sessionId) return;

      try {
        // Load session
        const sessionData = await sessionsService.getSession(sessionId);
        if (!sessionData) {
          setError('Session not found');
          setLoading(false);
          return;
        }

        // Load registration
        const registrationData = await sessionRegistrationsService.getStudentRegistration(sessionId, studentId);
        if (!registrationData) {
          setError('You are not registered for this session');
          setLoading(false);
          return;
        }

        // Check if registration is approved
        if (registrationData.status !== 'active') {
          setError('Your registration is not yet approved. Please wait for admin approval.');
          setLoading(false);
          return;
        }

        // Check if classroom is enabled
        if (!registrationData.classroomEnabled) {
          setError('Classroom access is not enabled for you. Please contact your administrator.');
          setLoading(false);
          return;
        }

        setSession(sessionData);
        setRegistration(registrationData);

        if (sessionData.courseId) {
          setCourseLoading(true);
          try {
            const courseData = await coursesService.getCourse(sessionData.courseId);
            if (courseData) {
              setCourse(courseData);
              setActiveTopicName((prev) => prev || courseData?.topics?.[0]?.name || '');
            } else if (sessionData.course) {
              const fallbackCourse = { id: sessionData.courseId, ...sessionData.course };
              setCourse(fallbackCourse);
              setActiveTopicName((prev) => prev || sessionData.course?.topics?.[0]?.name || '');
            } else {
              setCourse(null);
              setActiveTopicName('');
            }
          } catch (courseErr) {
            console.error('Error loading course for classroom:', courseErr);
            if (sessionData.course) {
              const fallbackCourse = { id: sessionData.courseId, ...sessionData.course };
              setCourse(fallbackCourse);
              setActiveTopicName((prev) => prev || sessionData.course?.topics?.[0]?.name || '');
            } else {
              setCourse(null);
              setActiveTopicName('');
            }
          } finally {
            setCourseLoading(false);
          }
        } else if (sessionData.course) {
          setCourse(sessionData.course);
          setActiveTopicName((prev) => prev || sessionData.course?.topics?.[0]?.name || '');
        } else {
          setCourse(null);
          setActiveTopicName('');
        }
      } catch (err) {
        console.error('Error loading classroom data:', err);
        setError('Error loading classroom. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadClassroomData();
  }, [studentId, sessionId]);

  useEffect(() => {
    setFeatureToggles(mergeSessionFeatureToggles(session?.featureToggles));
  }, [session]);

  useEffect(() => {
    // For students, always use interactive tab
    if (user?.type === 'student' && activeTab !== 'interactive') {
      setActiveTab('interactive');
      return;
    }
    
    if (!tabParam) return;
    if (!TAB_KEYS.includes(tabParam)) return;
    if (!isTabEnabled(tabParam)) return;
    if (tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam, activeTab, featureToggles, user?.type]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isTabEnabled(activeTab)) return;
    const fallback = TAB_KEYS.find((tab) => isTabEnabled(tab));
    if (fallback && fallback !== activeTab) {
      setActiveTab(fallback);
    }
  }, [featureToggles, activeTab, isTabEnabled]);

  useEffect(() => {
    if (!course?.topics?.length) return;
    
    // If topic is provided in URL, use it
    if (topicParam && course.topics.some((topic) => topic.name === topicParam)) {
      if (activeTopicName !== topicParam) {
        setActiveTopicName(topicParam);
      }
      return;
    }
    
    // Only set default topic if no topic is currently selected
    // Don't reset if user has already selected a topic
    if (!activeTopicName && course.topics.length > 0) {
      setActiveTopicName(course.topics[0].name);
    }
  }, [course, topicParam]); // Removed activeTopicName from dependencies to prevent reset loop

  const handleJoinClassroom = () => {
    if (session?.classroomUrl) {
      // Open classroom URL in new tab
      window.open(session.classroomUrl, '_blank');
    } else {
      alert('Classroom URL is not configured. Please contact your administrator.');
    }
  };

  if (loading) {
    return (
      <div className="classroom-container">
        <div className="classroom-header">
          <button onClick={() => navigate('/student/registered')} className="back-btn">
            ← Back to Sessions
          </button>
        </div>
        <div className="classroom-info">
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Loading classroom...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="classroom-container">
        <div className="classroom-header">
          <button onClick={() => navigate('/student/registered')} className="back-btn">
            ← Back to Sessions
          </button>
        </div>
        <div className="classroom-info">
          <div className="error-section">
            <div className="error-icon">⚠️</div>
            <h2>Unable to Load Classroom</h2>
            <p className="error-message">{error}</p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => window.location.reload()} className="retry-btn">
                🔄 Try Again
              </button>
              <button onClick={() => navigate('/student/registered')} className="back-btn">
                ← Back to Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const courseTopics = course?.topics || [];
  const activeTopic = courseTopics.find((topic) => topic.name === activeTopicName) || null;
  const interactiveCourseId = course?.id || session?.courseId || session?.course?.id || '';
  const availableTabs = TAB_KEYS.filter((tab) => isTabEnabled(tab));

  return (
    <div className="classroom-container">
      <div className="classroom-header">
        <button onClick={() => navigate('/student/registered')} className="back-btn">
          ← Back to Sessions
        </button>
        <h1>🎓 Live Classroom</h1>
      </div>

      {/* Classroom Tabs */}
      <div className="classroom-tabs">
        {availableTabs.length === 0 ? (
          <span className="tab-placeholder">All classroom tabs are disabled for this session.</span>
        ) : (
          availableTabs.map((tabKey) => (
            <button
              key={tabKey}
              className={`tab-btn ${activeTab === tabKey ? 'active' : ''}`}
              onClick={() => setActiveTab(tabKey)}
            >
              {TAB_LABELS[tabKey]}
            </button>
          ))
        )}
      </div>

      {/* Tab Content */}
      {availableTabs.length === 0 ? (
        <div className="tab-content">
          <div className="classroom-info">
            <div className="no-url-section">
              <p>⚠️ All classroom sections are currently disabled for this session. Please contact your instructor for guidance.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="tab-content">
          {activeTab === 'video' && (
          <div className="classroom-info">
            {/* Try native video first, fallback to external URL */}
            {(() => {
              // Check if Daily.co is configured
              const useNativeVideo = process.env.REACT_APP_DAILY_API_KEY;
              
              if (useNativeVideo) {
                // Use native video component
                return (
                  <LiveVideoClassroom
                    sessionId={sessionId}
                    userId={studentId}
                    userName={studentName}
                    userType={user?.type || 'student'}
                    isInstructor={user?.type === 'coach' || user?.type === 'admin'}
                  />
                );
              } else {
                // Fallback to external URL
                return (
                  <>
                    <div className="session-card-classroom">
                      <h2>{session.title}</h2>
                      <div className="session-details">
                        <p><strong>📅 Start:</strong> {new Date(session.startDate).toLocaleDateString()}</p>
                        <p><strong>📅 End:</strong> {new Date(session.endDate).toLocaleDateString()}</p>
                        <p><strong>⏱️ Duration:</strong> {session.totalDuration} hours</p>
                        {session.course && (
                          <p><strong>📚 Course:</strong> {session.course.title}</p>
                        )}
                      </div>
                    </div>

                    <div className="classroom-access">
                      <div className="access-status">
                        <div className="status-badge enabled">
                          ✅ Classroom Access Enabled
                        </div>
                        <p className="access-note">
                          You have been granted access to the live instructor-led classroom for this session.
                        </p>
                      </div>

                      {session.classroomUrl ? (
                        <div className="join-section">
                          <h3>🚀 Ready to Join?</h3>
                          <p>Click the button below to join the live classroom session.</p>
                          <button onClick={handleJoinClassroom} className="join-classroom-btn">
                            🎥 Join Live Classroom
                          </button>
                          <p className="classroom-url">
                            <strong>Classroom URL:</strong> 
                            <a href={session.classroomUrl} target="_blank" rel="noopener noreferrer">
                              {session.classroomUrl}
                            </a>
                          </p>
                        </div>
                      ) : (
                        <div className="no-url-section">
                          <p>⚠️ Classroom URL is being configured. Please check back soon or contact your instructor.</p>
                        </div>
                      )}
                    </div>

                    <div className="classroom-instructions">
                      <h3>📋 Instructions</h3>
                      <ul>
                        <li>Make sure you have a stable internet connection</li>
                        <li>Use headphones or earphones for better audio quality</li>
                        <li>Join a few minutes before the scheduled time</li>
                        <li>Keep your microphone muted when not speaking</li>
                        <li>Raise your hand or use chat to ask questions</li>
                        <li>If you experience technical issues, contact your instructor</li>
                      </ul>
                    </div>
                  </>
                );
              }
            })()}
          </div>
        )}

          {activeTab === 'livecode' && (
          <div className="livecode-container">
            <LiveCodeClassroom sessionId={sessionId} user={user} isCoach={false} />
          </div>
        )}

          {activeTab === 'interactive' && (
          <div className="interactive-container">
            <div className="interactive-header">
              <div>
                <h2>🎮 Interactive Classroom</h2>
                {course?.title && <p className="interactive-course-name">{course.title}</p>}
              </div>
              {interactiveStars > 0 && (
                <div className="interactive-stats">
                  ⭐ {interactiveStars} stars earned
                </div>
              )}
            </div>

            {courseLoading ? (
              <div className="interactive-loading">
                <p>Loading interactive content...</p>
              </div>
            ) : !courseTopics.length ? (
              <div className="coming-soon">
                <p>Interactive components for this course are still being prepared.</p>
                <p>Encourage your coach to add content in the Content Studio so you can practice here.</p>
              </div>
            ) : (
              <>
                <div className="interactive-topic-picker">
                  <label htmlFor="interactive-topic-select">Choose a topic</label>
                  <select
                    id="interactive-topic-select"
                    value={activeTopicName || ''}
                    onChange={(e) => {
                      const selectedTopic = e.target.value;
                      setActiveTopicName(selectedTopic);
                      // Update URL without page reload
                      const newSearchParams = new URLSearchParams(location.search);
                      newSearchParams.set('topic', selectedTopic);
                      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
                    }}
                  >
                    {courseTopics.map((topic) => (
                      <option key={topic.name} value={topic.name}>
                        {topic.name}
                      </option>
                    ))}
                  </select>
                  {activeTopic?.hours && (
                    <span className="topic-hours-chip">⏱️ {activeTopic.hours}h</span>
                  )}
                </div>

                {activeTopic ? (
                  <div className="interactive-content-panel">
                    <TopicInteractiveLearning
                      userId={studentId}
                      userName={studentName}
                      topic={activeTopic}
                      courseId={interactiveCourseId}
                      sessionFeatureToggles={featureToggles}
                      onProgressUpdate={(stars) => setInteractiveStars((prev) => prev + (stars || 0))}
                      isStudent={true}
                    />
                  </div>
                ) : (
                  <div className="coming-soon">
                    <p>Select a topic to load its quizzes, exercises, and slides.</p>
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Classroom;

